import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { memberApi } from '@formosa/shared/api/member';
import { ApiError } from '@formosa/shared/api/createClient';
import { GOOGLE_CLIENT_ID, getInviterCode } from '@formosa/shared/constants/auth';
import { mapGoogleLocale } from '@formosa/shared/utils/googleLocale';
import { useTravelTranslation } from '../app/TravelLocaleContext';
import { useAuth } from './AuthContext';
import { authReturnDestination } from './authReturn';

/**
 * Travel-owned Google Identity Services button.
 * Reuses Member OAuth contract only (id_token → registerLoginGoogleCallback).
 */
export default function GoogleSignInButton({ onError, mode: _mode = 'login' }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { completeAuth } = useAuth();
  const { locale, t } = useTravelTranslation();
  const containerRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [gsiReady, setGsiReady] = useState(false);
  const destination = authReturnDestination(location.state?.from);
  /** Login／Register 共用既有 registerLoginGoogleCallback；文案統一「使用 Google 繼續」。 */
  const label = t('travel.auth.googleContinue');
  // 回呼可能已註冊給 Google SDK；以 ref 取最新翻譯，不因切換語系重建回呼
  const tRef = useRef(t);
  tRef.current = t;
  const gsiButtonText = 'continue_with';
  const configGap = !GOOGLE_CLIENT_ID;

  const handleGoogleResponse = useCallback(
    async (response) => {
      if (!response?.credential) {
        onError?.(tRef.current('travel.auth.googleCredentialMissing'));
        return;
      }
      setLoading(true);
      onError?.('');
      try {
        const data = await memberApi.googleCallback({
          idToken: response.credential,
          inviterCode: getInviterCode() || undefined,
        });
        await completeAuth(data);
        navigate(destination, { replace: true });
      } catch (err) {
        onError?.(err instanceof ApiError ? err.message : tRef.current('travel.auth.googleFailed'));
      } finally {
        setLoading(false);
      }
    },
    [completeAuth, destination, navigate, onError],
  );

  useEffect(() => {
    if (configGap) return undefined;
    let cancelled = false;
    let intervalId;

    const initGoogle = () => {
      if (cancelled || !window.google?.accounts?.id || !containerRef.current) return;
      setGsiReady(false);
      containerRef.current.innerHTML = '';
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleGoogleResponse,
        auto_select: false,
        cancel_on_tap_outside: true,
        locale: mapGoogleLocale(locale),
      });
      const width = containerRef.current.parentElement?.offsetWidth || 360;
      window.google.accounts.id.renderButton(containerRef.current, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        shape: 'rectangular',
        text: gsiButtonText,
        logo_alignment: 'left',
        width: Math.min(Math.max(width, 280), 400),
      });
      if (!cancelled) setGsiReady(true);
    };

    if (window.google?.accounts?.id) {
      initGoogle();
    } else {
      intervalId = setInterval(() => {
        if (window.google?.accounts?.id) {
          clearInterval(intervalId);
          initGoogle();
        }
      }, 100);
    }

    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
    };
  }, [configGap, handleGoogleResponse, locale, gsiButtonText]);

  if (configGap) {
    return (
      <p className="travel-auth__hint" role="status">
        {t('travel.auth.googleUnavailable')}
      </p>
    );
  }

  return (
    <div
      className={[
        'travel-auth__google',
        gsiReady ? 'travel-auth__google--ready' : '',
        loading ? 'travel-auth__google--loading' : '',
      ].filter(Boolean).join(' ')}
    >
      <div className="travel-auth__google-face" aria-hidden="true">
        <GoogleIcon />
        <span>{loading ? t('travel.auth.googleConnecting') : label}</span>
      </div>
      <div
        ref={containerRef}
        className="travel-auth__google-overlay"
        aria-label={label}
        aria-busy={loading}
      />
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden>
      <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303C33.654 32.657 29.083 36 24 36c-5.514 0-10-4.486-10-10s4.486-10 10-10c2.521 0 4.817.932 6.584 2.466l6.082-6.082C33.455 9.298 28.978 7 24 7 13.507 7 5 15.507 5 26s8.507 19 19 19 19-8.507 19-19c0-1.276-.131-2.522-.389-3.717z" />
      <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 16.108 18.961 13 24 13c2.521 0 4.817.932 6.584 2.466l6.082-6.082C33.455 9.298 28.978 7 24 7 16.318 7 9.656 11.337 6.306 14.691z" />
      <path fill="#4CAF50" d="M24 45c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 36.091 26.715 37 24 37c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 45 24 45z" />
      <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571l6.19 5.238C42.022 35.026 45 30.675 45 26c0-1.276-.131-2.522-.389-3.717z" />
    </svg>
  );
}
